import { AccountCircle, AccountCircleOutlined } from '@mui/icons-material';
import {
  Button,
  Container,
  Toolbar,
  Typography,
  MenuItem,
  IconButton,
  Box,
} from '@mui/material';
import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';

import { ReactComponent as RabbitLogo } from 'assets/SVGs/logo.svg';
import { Breadcrumb } from 'components/Breadcrumb';
import { Masquarade } from 'components/Masquarade';
import { MasquaradeBanner } from 'components/MasquaradeBanner';
import RoleSelectionModal from 'components/RoleSelection/RoleSelectionModal';
import { settings } from 'config';
import { convertUTCDateTimeToDateTime, getLastSegmentOfPath } from 'helpers';
import { useBreadcrumbContext, useUserContext } from 'hooks';
import { Maybe } from 'types';

import { EndMasquaradeButton } from './EndMasquaradeButton';
import {
  RabbitAppBar,
  Link,
  LinkWrapper,
  BreadcrumbRow,
} from './styledComponents';

interface LinkProps {
  path: string;
  label: string;
}

export const Navbar = (props: {
  links: Array<LinkProps>;
  profile: Maybe<boolean>;
}) => {
  const { breadcrumbData } = useBreadcrumbContext();
  const { user, userWithRoles } = useUserContext();
  const { oktaAuth } = useOktaAuth();

  //Get the number of roles for current user
  const numOfRoles = userWithRoles?.roles?.length;
  const activeRole = userWithRoles?.role;
  const signOut = async () => {
    await oktaAuth.signOut({
      clearTokensBeforeRedirect: true,
      postLogoutRedirectUri: settings.APP_ORIGINAL_URI,
    });
  };
  const { links, profile } = props;

  const history = useHistory();

  const previous_login: string = user?.previous_login ?? '';
  const previousLogin = convertUTCDateTimeToDateTime(previous_login);

  return (
    <>
      <RabbitAppBar position='static' role='banner' aria-label='Navbar'>
        <Container maxWidth={false} sx={{ display: 'flex' }}>
          <Toolbar
            disableGutters
            sx={{ width: '100%' }}
            role='menubar'
            aria-label='Navbar'
          >
            <div>
              <RabbitLogo />
            </div>
            <Typography
              variant='h5'
              noWrap
              component='div'
              sx={{
                mr: 2,
                ml: 5,
                letterSpacing: '.2em',
                display: { md: 'flex' },
              }}
            >
              RABBIT
            </Typography>
            {links.map((link) => (
              <LinkWrapper
                key={link.label}
                role='menuitem'
                aria-label={link.label}
              >
                <Link to={link.path} exact activeClassName='current'>
                  {link.label}
                </Link>
              </LinkWrapper>
            ))}
            {numOfRoles !== undefined &&
            numOfRoles >= 2 &&
            !user?.is_assuming_role ? (
              <MenuItem role='menuitem' aria-label='role selection'>
                <RoleSelectionModal />
              </MenuItem>
            ) : null}
            <MenuItem
              sx={{ border: '2px red dotted', margin: '8px' }}
              role='menuitem'
              aria-label='active role'
            >
              <Typography variant='body1' padding={1}>
                Active Role: {activeRole}
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{
                marginLeft: 'auto',
                display: 'flex',
                flexDirection: 'row',
              }}
              role='menuitem'
              aria-label='previous login'
            >
              <Box>
                <Typography
                  variant='body1'
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  Previous login: {previousLogin}
                </Typography>
              </Box>
            </MenuItem>
            {user?.role === 'candidate_admin' && (
              <MenuItem role='menuitem' aria-label='start masquarade'>
                <Masquarade sx={{ color: 'white' }} />
              </MenuItem>
            )}
            {user?.is_assuming_role && <EndMasquaradeButton />}
            <MenuItem>
              <Button
                variant='text'
                sx={{ color: 'white' }}
                onClick={signOut}
                role='button'
                aria-label='sign out button'
              >
                Sign Out
              </Button>
            </MenuItem>
            {profile && (
              <MenuItem sx={{ padding: 0 }} role='menuitem'>
                <IconButton
                  sx={{ color: 'white' }}
                  aria-label='view profile'
                  onClick={() => {
                    history.push('me');
                  }}
                >
                  {getLastSegmentOfPath(history.location.pathname) === 'me' ? (
                    <AccountCircle />
                  ) : (
                    <AccountCircleOutlined />
                  )}
                </IconButton>
              </MenuItem>
            )}
          </Toolbar>
        </Container>
      </RabbitAppBar>
      {user?.is_assuming_role && <MasquaradeBanner />}
      {breadcrumbData && (
        <BreadcrumbRow role='group'>
          <Breadcrumb {...breadcrumbData} />
        </BreadcrumbRow>
      )}
    </>
  );
};
